import axios, { AxiosError } from 'axios';
import { toast } from 'react-toastify';
import { isPublicAuthPath } from './subdomain';

// ── Global Read-Only Mode ────────────────────────────────────────────────
// When enabled, all write operations (POST, PUT, PATCH, DELETE) are blocked
// before they reach the server. Set via setGlobalReadOnly() from ReadOnlyProvider.
let _globalReadOnly = false;

export function setGlobalReadOnly(value: boolean) {
  _globalReadOnly = value;
}

export function isGlobalReadOnly(): boolean {
  return _globalReadOnly;
}

// ── Route-Level Permission Check (defense-in-depth) ──────────────────────
// Checks permissions directly at request time, independent of React lifecycle.
// This prevents the timing gap where _globalReadOnly may briefly be false
// during route transitions.
let _currentAdminRole: string | null = null;
let _isReadOnlyForRoute: ((role: string, path: string) => boolean) | null = null;

/** Set the current admin role (called from TenantAuthContext on login/auth). */
export function setCurrentAdminRole(role: string | null) {
  _currentAdminRole = role;
}

/** Register the route-level read-only checker (called from permissions.ts). */
export function registerReadOnlyChecker(fn: (role: string, path: string) => boolean) {
  _isReadOnlyForRoute = fn;
}

/**
 * Determine if write operations should be blocked.
 * Uses two checks:
 *   1. The global flag (set by ReadOnlyProvider — covers section-level overrides like settings:aeo)
 *   2. Direct route-level permission check (defense-in-depth, no React lifecycle dependency)
 */
function isWriteBlocked(): boolean {
  // Check 1: global flag from ReadOnlyProvider
  if (_globalReadOnly) return true;

  // Check 2: route-level permission check at request time
  if (_currentAdminRole && _isReadOnlyForRoute) {
    const currentPath = window.location.pathname;
    if (_isReadOnlyForRoute(_currentAdminRole, currentPath)) return true;
  }

  return false;
}

// Methods that mutate data on the server
const WRITE_METHODS = ['post', 'put', 'patch', 'delete'];

// Paths that should always be allowed even in read-only mode (e.g., auth)
const ALWAYS_ALLOWED_PATHS = ['/auth/', '/login', '/forgot-password', '/reset-password'];

const TENANT_API_BASE_URL = import.meta.env.VITE_TENANT_API_URL || 'https://app-lawma-prod-ai-api-westus2-001-g9cshwd4bvhyh3bj.westus2-01.azurewebsites.net';
const AUTH_API_BASE_URL = import.meta.env.VITE_TENANT_AUTH_API_URL || 'https://app-lawma-prod-mgmt-api-westus2-001-eyewdjame4d7e6ay.westus2-01.azurewebsites.net/api/v1';

// Create axios instance for tenant API calls
export const apiClient = axios.create({
  baseURL: TENANT_API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Create axios instance for auth API calls
export const authApiClient = axios.create({
  baseURL: AUTH_API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Shared write-blocking interceptor — used by both apiClient and authApiClient
function createWriteBlockInterceptor() {
  return (config: any) => {
    if (
      isWriteBlocked() &&
      config.method &&
      WRITE_METHODS.includes(config.method.toLowerCase()) &&
      !ALWAYS_ALLOWED_PATHS.some((p: string) => config.url?.includes(p))
    ) {
      toast.warn('Read-only mode: This action is not allowed', { toastId: 'read-only-block', autoClose: 3000 });
      return Promise.reject(new axios.Cancel('Blocked by read-only mode'));
    }
    return config;
  };
}

// Read-only interceptor for apiClient — blocks write requests
apiClient.interceptors.request.use(createWriteBlockInterceptor(), (error) => Promise.reject(error));

// Read-only interceptor for authApiClient — blocks write requests (except login)
authApiClient.interceptors.request.use(createWriteBlockInterceptor(), (error) => Promise.reject(error));

// Request interceptor to add auth token and tenant ID for apiClient
apiClient.interceptors.request.use(
  (config) => {
    const currentAuthToken = localStorage.getItem('tenant_auth_token');
    const currentTenantId = localStorage.getItem('tenant_id');
    if (currentAuthToken) {
      config.headers.Authorization = `Bearer ${currentAuthToken}`;
    }
    if (currentTenantId) {
      config.headers['X-Tenant-ID'] = currentTenantId;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Request interceptor to add auth token for authApiClient
authApiClient.interceptors.request.use(
  (config) => {
    const currentAuthToken = localStorage.getItem('tenant_auth_token');
    if (currentAuthToken) {
      config.headers.Authorization = `Bearer ${currentAuthToken}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Function to clear ALL tenant data (no fallbacks)
export const clearAllTenantData = () => {
  // Clear all localStorage items
  localStorage.removeItem('tenant_auth_token');
  localStorage.removeItem('tenant_id');
  localStorage.removeItem('tenant_info');

  // Clear any other possible cached data
  const keysToRemove = [];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key && (key.startsWith('tenant_') || key.includes('cache_'))) {
      keysToRemove.push(key);
    }
  }
  keysToRemove.forEach(key => localStorage.removeItem(key));

  // Clear sessionStorage as well
  const sessionKeysToRemove = [];
  for (let i = 0; i < sessionStorage.length; i++) {
    const key = sessionStorage.key(i);
    if (key && (key.startsWith('tenant_') || key.includes('cache_'))) {
      sessionKeysToRemove.push(key);
    }
  }
  sessionKeysToRemove.forEach(key => sessionStorage.removeItem(key));
};

// Response interceptor to handle auth errors for both clients
const handleAuthError = (error: AxiosError) => {
  if (error.response?.status === 401 || error.response?.status === 403) {
    // CRITICAL: Clear ALL tenant data immediately (no fallbacks)
    clearAllTenantData();

    if (!isPublicAuthPath()) {
      window.location.href = '/login';
    }
  }
  return Promise.reject(error);
};

// Enhanced interceptor that also handles network errors
const handleNetworkError = (error: any) => {
  // Ignore cancelled requests (e.g., from read-only mode interceptor)
  if (axios.isCancel(error)) {
    return Promise.reject(error);
  }
  // If it's a network error and we're not on login page, clear data
  if (!error.response && !isPublicAuthPath()) {
    console.warn('Network error detected, clearing cached data to prevent stale tenant data usage');
    clearAllTenantData();
  }
  return handleAuthError(error);
};

apiClient.interceptors.response.use((response) => response, handleNetworkError);
authApiClient.interceptors.response.use((response) => response, handleNetworkError);

function messageFromDetail(detail: unknown): string | null {
  if (detail == null) return null;
  if (typeof detail === 'string') return detail;
  if (typeof detail === 'object') {
    const msg = (detail as { message?: string }).message;
    return typeof msg === 'string' ? msg : JSON.stringify(detail);
  }
  return JSON.stringify(detail);
}

/**
 * Get a user-facing error message from an error (e.g. AxiosError).
 * Priority: response.data.detail → response.statusText → response.message / error.message → fallback.
 */
export function getErrorMessage(error: unknown): string {
  if (error == null) return 'An error occurred';
  const e = error as Record<string, unknown>;
  const response = e?.response as Record<string, unknown> | undefined;
  const data = response?.data as Record<string, unknown> | undefined;
  const fromDetail = messageFromDetail(data?.detail);
  if (fromDetail != null) return fromDetail;
  const statusText = response?.statusText;
  if (statusText != null && typeof statusText === 'string') return statusText;
  const responseMessage = response?.message;
  if (responseMessage != null && typeof responseMessage === 'string') return responseMessage;
  const message = e?.message;
  if (message != null && typeof message === 'string') return message;
  if (error instanceof Error) return error.message;
  if (typeof error === 'string') return error;
  return 'An error occurred';
}

// Types
